import { useMemo, useState, type ReactNode } from "react"
import {
  BadgeCheck,
  Building2,
  CalendarDays,
  CheckCircle2,
  Copy,
  ExternalLink,
  MapPin,
  Phone,
  Shield,
  Sparkles,
  Store,
  User,
  WalletCards,
} from "lucide-react"
import { useSelector } from "react-redux"
import { getStoreCategoryLabel } from "../../utils/storeOptions"
import { buildStorefrontShortUrl, buildStorefrontUrl } from "../../utils/storefront"
import { isCarStore } from "../../utils/storeCategory"
import StorefrontLiveQrTools from "../../components/Storefront/StorefrontLiveQrTools"
import StorefrontStatsCard from "../../components/Storefront/StorefrontStatsCard"
import StorefrontPublicOrderCard from "../../components/Storefront/StorefrontPublicOrderCard"

export default function Profile() {
  const { userdata } = useSelector((state: any) => state.UserReducer)
  const user = userdata?.user || {}
  const store = userdata?.store || {}
  const subscription = userdata?.subscription || {}
  const planDetails = subscription?.planDetails || user?.planDetails || {}
  const billingCycle = subscription?.billingCycle || user?.subscriptionBillingCycle || "monthly"
  const [copied, setCopied] = useState(false)
  const [copiedDirect, setCopiedDirect] = useState(false)
  const [copiedStoreId, setCopiedStoreId] = useState(false)

  const initials = useMemo(() => {
    return String(user?.fullName || user?.name || "GU")
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("")
  }, [user?.fullName, user?.name])

  const storefrontShortUrl = buildStorefrontShortUrl(store)
  const storefrontUrl = buildStorefrontUrl(store)
  const storeId = String(store?._id || store?.id || "")
  const carStore = isCarStore(store)

  const joinedDate = user?.createdAt
    ? new Date(user.createdAt).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : null

  const copyValue = async (
    value: string,
    onCopied: (value: boolean) => void
  ) => {
    if (!value || typeof navigator === "undefined" || !navigator.clipboard) return

    try {
      await navigator.clipboard.writeText(value)
      onCopied(true)
      window.setTimeout(() => onCopied(false), 2000)
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <div className="space-y-8">
      <section className="relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-cyan-600 via-sky-700 to-blue-900 p-6 text-white shadow-xl sm:p-7">
        <div className="pointer-events-none absolute -right-10 -top-8 h-40 w-40 rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-12 -left-8 h-36 w-36 rounded-full bg-cyan-200/15 blur-3xl" />

        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex items-start gap-4">
            <div className="flex h-20 w-20 items-center justify-center rounded-[28px] bg-white/15 text-2xl font-bold shadow-lg backdrop-blur-sm">
              {initials || "GU"}
            </div>
            <div>
              <p className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-white/90">
                <Sparkles size={12} />
                Mon espace
              </p>
              <h1 className="mt-3 text-2xl font-bold sm:text-3xl">{user?.fullName || user?.name || "Utilisateur Gestock"}</h1>
              <p className="mt-2 max-w-2xl text-sm text-white/85">
                Consultez vos informations de compte, votre boutique active et votre formule
                d’abonnement depuis un seul écran.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <HeroStat
              label="Rôle"
              value={user?.role === "ADMIN" ? "Administrateur" : user?.role || "-"}
              icon={<Shield size={16} />}
            />
            <HeroStat
              label="Statut"
              value={user?.active === false ? "Inactif" : "Actif"}
              icon={<BadgeCheck size={16} />}
            />
            <HeroStat
              label="Formule"
              value={subscription?.currentPlan || user?.plan || "FREE"}
              icon={<WalletCards size={16} />}
            />
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <KpiCard
          label="Plan actuel"
          value={planDetails?.name || subscription?.currentPlan || "Gratuit"}
          hint={planDetails?.price ? `${Number(planDetails.price).toLocaleString()} FCFA / ${billingCycle === "yearly" ? "an" : "mois"}` : "Formule active"}
          icon={<WalletCards size={18} />}
          tone="from-blue-600 to-cyan-600"
        />
        <KpiCard
          label="Boutique active"
          value={store?.name || "-"}
          hint={store?.city || "Aucune ville renseignée"}
          icon={<Store size={18} />}
          tone="from-emerald-600 to-green-500"
        />
        <KpiCard
          label="Compte créé"
          value={joinedDate || "-"}
          hint="Date d’activation du compte"
          icon={<CalendarDays size={18} />}
          tone="from-amber-500 to-orange-600"
        />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.15fr_0.85fr]">
        <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white px-6 py-5">
            <p className="text-sm font-semibold text-slate-800">Informations du compte</p>
            <p className="mt-1 text-xs text-slate-500">
              Détails personnels utilisés pour votre accès à Gestock.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2">
            <InfoCard icon={<User size={18} />} label="Nom complet" value={user?.fullName || user?.name || "-"} />
            <InfoCard icon={<Phone size={18} />} label="Téléphone" value={user?.phone || "-"} />
            <InfoCard
              icon={<Shield size={18} />}
              label="Rôle"
              value={user?.role === "ADMIN" ? "Administrateur" : user?.role || "-"}
            />
            <InfoCard
              icon={<CheckCircle2 size={18} />}
              label="État du compte"
              value={user?.active === false ? "Compte inactif" : "Compte actif"}
            />
          </div>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white px-6 py-5">
            <p className="text-sm font-semibold text-slate-800">Boutique & abonnement</p>
            <p className="mt-1 text-xs text-slate-500">
              Vue rapide sur la boutique actuellement rattachée à votre compte.
            </p>
          </div>

          <div className="space-y-4 p-6">
            <InfoRow icon={<Building2 size={18} />} label="Boutique active" value={store?.name || "-"} />
            <InfoRow icon={<MapPin size={18} />} label="Ville" value={store?.city || "Non renseignée"} />
            <InfoRow
              icon={<Store size={18} />}
              label="Type"
              value={getStoreCategoryLabel(store?.category || store?.type || "GENERAL")}
            />
            <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4">
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-blue-600 shadow-sm">
                  <ExternalLink size={18} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs uppercase tracking-[0.12em] text-slate-400">
                    {carStore ? "Parc auto public" : "Vitrine publique"}
                  </p>
                  <p className="mt-2 text-xs uppercase tracking-[0.12em] text-slate-400">
                    Store ID
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <p className="break-all text-sm font-semibold text-slate-800">
                      {storeId || "Identifiant indisponible"}
                    </p>
                    <button
                      type="button"
                      onClick={() => copyValue(storeId, setCopiedStoreId)}
                      disabled={!storeId}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Copy size={14} />
                      {copiedStoreId ? "Copié" : "Copier l'ID"}
                    </button>
                  </div>
                  <p className="mt-1 break-all text-sm font-semibold text-slate-800">{storefrontShortUrl || "Lien indisponible"}</p>
                  {storefrontUrl && storefrontShortUrl && storefrontUrl !== storefrontShortUrl ? (
                    <p className="mt-2 break-all text-xs text-slate-500">Lien direct: {storefrontUrl}</p>
                  ) : null}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => copyValue(storefrontShortUrl, setCopied)}
                      disabled={!storefrontShortUrl}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Copy size={14} />
                      {copied ? "Copié" : "Copier le lien court"}
                    </button>
                    <a
                      href={storefrontShortUrl || "#"}
                      target="_blank"
                      rel="noreferrer"
                      className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold text-white transition ${
                        storefrontShortUrl ? "bg-slate-900 hover:bg-slate-800" : "pointer-events-none bg-slate-300"
                      }`}
                    >
                      <ExternalLink size={14} />
                      Ouvrir
                    </a>
                    {storefrontUrl && storefrontUrl !== storefrontShortUrl ? (
                      <button
                        type="button"
                        onClick={() => copyValue(storefrontUrl, setCopiedDirect)}
                        className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-100"
                      >
                        <Copy size={14} />
                        {copiedDirect ? "Copié" : "Copier le lien direct"}
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
            <StorefrontLiveQrTools store={store} />
            <StorefrontPublicOrderCard
              store={store}
              defaultContactName={user?.fullName || user?.name || ""}
              defaultWhatsappNumber={user?.phone || ""}
            />
            <StorefrontStatsCard store={store} />
            <InfoRow
              icon={<WalletCards size={18} />}
              label="Abonnement"
              value={planDetails?.name || subscription?.currentPlan || "Gratuit"}
            />

            <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
              <p className="text-sm font-semibold text-blue-800">Résumé du plan</p>
              <p className="mt-2 text-sm text-blue-700">
                {subscription?.currentPlan === "FREE" || (!subscription?.currentPlan && !user?.plan)
                  ? "Plan gratuit actif. Certaines fonctionnalités avancées restent limitées."
                  : `Plan ${planDetails?.name || subscription?.currentPlan || user?.plan} actif pour votre boutique.`}
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

function HeroStat({
  label,
  value,
  icon,
}: {
  label: string
  value: string
  icon: ReactNode
}) {
  return (
    <div className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3 backdrop-blur-sm">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-white/70">
        {icon}
        {label}
      </div>
      <p className="mt-2 text-sm font-semibold text-white">{value}</p>
    </div>
  )
}

function KpiCard({
  label,
  value,
  hint,
  icon,
  tone,
}: {
  label: string
  value: string
  hint: string
  icon: ReactNode
  tone: string
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm text-slate-500">{label}</p>
          <p className="mt-2 text-xl font-bold text-slate-800">{value}</p>
          <p className="mt-1 text-xs text-slate-500">{hint}</p>
        </div>
        <div className={`flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br ${tone} text-white shadow-lg`}>
          {icon}
        </div>
      </div>
    </div>
  )
}

function InfoCard({
  icon,
  label,
  value,
}: {
  icon: ReactNode
  label: string
  value: string
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white text-blue-600 shadow-sm">
          {icon}
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.12em] text-slate-400">{label}</p>
          <p className="mt-1 text-base font-semibold text-slate-800">{value}</p>
        </div>
      </div>
    </div>
  )
}

function InfoRow({
  icon,
  label,
  value,
}: {
  icon: ReactNode
  label: string
  value: string
}) {
  return (
    <div className="flex items-start gap-4 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-blue-600 shadow-sm">
        {icon}
      </div>
      <div>
        <p className="text-sm text-slate-500">{label}</p>
        <p className="mt-1 text-base font-semibold text-slate-800">{value}</p>
      </div>
    </div>
  )
}
